import React, { useState } from 'react';
import { Button, FileUploader, Textarea, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as ErrorIcon } from 'assets/icons/error.svg';
import LocalModal from 'components/base/local-modal';
import { UPDATE_PAYMENT_ACTIONS } from 'models/payment-tracker.model';
import { ENUM_PAYMENT_STATUS } from 'models/common.model';
import { FILE_UPLOADER_TOKEN, MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';

type Props = {
  isOpen: boolean;
  onRequestClose: () => void;
  modalType: UPDATE_PAYMENT_ACTIONS;
};

const UpdatePaymentModal = ({ isOpen, onRequestClose, modalType }: Props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    reason: '',
    file_url: ''
  });

  const [isLoading, setIsloading] = useState(false);
  const handleSubmit = async () => {
    if (!id) {
      toast(<ToastMessage message='Missing payment request id' type='error' />);
      return;
    }

    setIsloading(true);
    let payload: { data: any; success: string } = { data: null, success: '' };

    switch (modalType) {
      case UPDATE_PAYMENT_ACTIONS.APPROVE:
        payload = {
          data: { status: ENUM_PAYMENT_STATUS.APPROVED },
          success: 'Payment approved successfully!'
        };
        break;
      case UPDATE_PAYMENT_ACTIONS.REJECT:
        payload = {
          data: { status: ENUM_PAYMENT_STATUS.REJECTED, note: formData.reason },
          success: 'Payment rejected successfully!'
        };
        break;
      case UPDATE_PAYMENT_ACTIONS.COMPLETE:
        payload = {
          data: { status: 'PAID', receipt_url: formData.file_url },
          success: 'Payment completed successfully!'
        };
        break;
      default:
        setIsloading(false);
        return;
    }

    try {
      const { data, success } = payload;
      const response = await apiService.put(`payment-request/${id}`, {
        version: 'v2',
        data
      });
      if (response) {
        toast(<ToastMessage message={success} type='success' />);
        onRequestClose?.();
        navigate(MAIN_STORE_ROUTE_KEYS.PAYMNET_TRACKER);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error updating payment status'}
          type='error'
        />
      );
    } finally {
      setIsloading(false);
    }
  };

  const paymentContent = {
    [UPDATE_PAYMENT_ACTIONS.APPROVE]: {
      title: '',
      body: 'Are you sure you want to proceed?',
      label: 'This action will confirm approval for payment',
      placeholder: '',
      fileText: ''
    },
    [UPDATE_PAYMENT_ACTIONS.REJECT]: {
      title: 'Add reason',
      body: '',
      label: '',
      placeholder: 'Drop your reasons for rejection',
      fileText: ''
    },
    [UPDATE_PAYMENT_ACTIONS.COMPLETE]: {
      title: 'Complete payment',
      body: '',
      label: '',
      placeholder: '',
      fileText: 'Upload receipt'
    },

    [UPDATE_PAYMENT_ACTIONS.NONE]: {
      title: '',
      body: '',
      label: '',
      placeholder: '',
      fileText: ''
    }
  };
  const { title, body, label, placeholder, fileText } = paymentContent[modalType];
  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => {
      return {
        ...prevFormData,
        [name]: value
      };
    });
  };

  return (
    <LocalModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentClassName='w-[80vw] max-w-[500px]'
      {...{ title }}>
      <div className='p-6 py-0 flex flex-col justify-center items-center'>
        {body ? (
          <>
            <ErrorIcon className='mb-3' />
            <h4 className='text-[20px] font-semibold text-[#2E3031] mb-4 text-center'>{body}</h4>
          </>
        ) : null}
        {label ? <p className='text-sm text-[#6F7376] mb-6'>{label}</p> : null}

        {placeholder ? (
          <div className='w-full'>
            <Textarea
              name='reason'
              value={formData.reason}
              onChange={handleChange}
              placeholder={placeholder}
              design='bordered'
              className='w-full'
              // className='w-full !border-b-[2px] !border-b-[#D5D6D7] bg-[#F4F5F5] !rounded-none my-4'
            />
          </div>
        ) : null}

        {fileText ? (
          <div className='w-full'>
            <FileUploader
              label={fileText}
              fileUrls={formData.file_url ? [formData.file_url] : []}
              setFileUrls={(imageUrls: any) => {
                setFormData((prevFormData) => {
                  return {
                    ...prevFormData,
                    file_url: imageUrls[0] ?? ''
                  };
                });
              }}
              token={FILE_UPLOADER_TOKEN}
              className='w-full'
            />
          </div>
        ) : null}

        <div className='flex gap-4 justify-center mt-3'>
          <Button
            variant='block'
            color='gray'
            onClick={onRequestClose}
            className='text-sm mr-4 hover:text-[#FF0000] transition duration-300 h-[41px] w-[139px] !bg-[#F2F2F2] !text-gray-6 !border-[#F2F2F2]'>
            Cancel
          </Button>
          <Button
            variant='block'
            color='primary'
            onClick={handleSubmit}
            disabled={
              isLoading ||
              (modalType === UPDATE_PAYMENT_ACTIONS.COMPLETE && !formData.file_url) ||
              (modalType === UPDATE_PAYMENT_ACTIONS.REJECT && !formData.reason)
            }
            loading={isLoading}
            className='text-sm text-[#FF0000] hover:text-[#FF0000] transition duration-300 h-[41px] w-[139px]'>
            Confirm
          </Button>
        </div>
      </div>
    </LocalModal>
  );
};

export default UpdatePaymentModal;
